import { Link, useLocation, useNavigate } from "react-router-dom";
import { CreditCard, Smartphone, Wallet, Banknote, Lock, ChevronRight, ShieldCheck, BadgeCheck, RefreshCcw, CheckCircle2, Copy, Tag as TagIcon, Sparkles, Gift } from "lucide-react";
import { useEffect, useState } from "react";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { trackBeginCheckout } from "@/lib/analyticsEvents";
import { toast } from "sonner";
import BrandLogo from "@/components/icons/BrandLogo";

const PAYMENT_METHODS = [
  { v: "card", l: "Card bancar", I: CreditCard },
  { v: "apple", l: "Apple Pay", I: Smartphone },
  { v: "google", l: "Google Pay", I: Smartphone },
  { v: "revolut", l: "Revolut", I: Wallet },
  { v: "wise", l: "Wise", I: Wallet },
  { v: "transfer", l: "Transfer bancar", I: Banknote },
];

// Iconițele rămân în cod (mapate pe poziție); titlul + textul insignelor și
// numărul de zile de garanție se editează din Setări → checkout.
const TRUST_ICONS = [ShieldCheck, BadgeCheck, RefreshCcw, CheckCircle2];
const TRUST_BADGES_FALLBACK = [
  { title: "Plată 100% securizată", text: "Procesare prin Stripe / 3D Secure." },
  { title: "Factură automată", text: "Primești PDF pe email instant." },
  { title: "Garanție 14 zile", text: "Banii înapoi dacă nu ești mulțumit." },
  { title: "Acces imediat", text: "Cursul tău e pregătit instant." },
];

function BankTransferDetails({ bank }) {
  const copy = (val) => {
    navigator.clipboard.writeText(val);
    toast.success("Copiat în clipboard");
  };
  if (!bank?.iban) {
    return (
      <p className="text-xs italic" style={{ color: "var(--ct-text-muted)" }}>
        Datele bancare nu sunt configurate momentan. Contactează pe WhatsApp pentru transfer.
      </p>
    );
  }
  return (
    <div className="space-y-2.5" data-testid="bank-transfer-details">
      {bank.beneficiary && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--ct-text-dim)" }}>Beneficiar</p>
          <p className="text-sm font-medium flex items-center gap-2">{bank.beneficiary}
            <button type="button" onClick={() => copy(bank.beneficiary)} className="opacity-50 hover:opacity-100"><Copy size={11} /></button>
          </p>
        </div>
      )}
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--ct-text-dim)" }}>IBAN</p>
        <p className="text-sm font-mono flex items-center gap-2 break-all">{bank.iban}
          <button type="button" onClick={() => copy(bank.iban)} className="opacity-50 hover:opacity-100" data-testid="copy-iban"><Copy size={11} /></button>
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {bank.bank_name && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--ct-text-dim)" }}>Banca</p>
            <p className="text-sm">{bank.bank_name}</p>
          </div>
        )}
        {bank.bic && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--ct-text-dim)" }}>BIC / SWIFT</p>
            <p className="text-sm font-mono">{bank.bic}</p>
          </div>
        )}
      </div>
      {bank.ref_template && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--ct-text-dim)" }}>Referință</p>
          <p className="text-sm">{bank.ref_template}</p>
        </div>
      )}
      {bank.instructions && (
        <p className="text-xs leading-relaxed mt-3 p-2.5 rounded-lg" style={{ background: "var(--ct-bg-soft)", color: "var(--ct-text-muted)" }}>
          {bank.instructions}
        </p>
      )}
    </div>
  );
}

export default function Checkout() {
  const { site } = useSite();
  const checkoutCfg = site?.checkout || {};
  const trustBadges = Array.isArray(checkoutCfg.trust_badges) && checkoutCfg.trust_badges.length ? checkoutCfg.trust_badges : TRUST_BADGES_FALLBACK;
  const guaranteeDays = Number(checkoutCfg.guarantee_days ?? 14);
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh: refreshAuth } = useAuth();
  const brand = site?.brand?.name || "Terapeut Georgiana";
  const bank = site?.bank_transfer || {};
  const [method, setMethod] = useState("card");
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "România" });
  const [gift, setGift] = useState({ on: false, email: "", name: "", message: "" });
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(null);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const search = new URLSearchParams(location.search);
  const product = search.get("course") || search.get("event") || "";
  const baseAmount = parseFloat(search.get("amount") || "0") || 0;
  const itemType = search.get("course") ? "course" : (search.get("event") ? "live" : "");
  const itemId = search.get("course") || search.get("event") || "";
  const total = discount ? discount.total_after : baseAmount;
  const isFreeCheckout = total === 0 && baseAmount > 0 && discount?.is_free;

  // Rezumat comandă: aducem titlul + coperta reală a cursului (nu slug-ul brut din URL).
  const [item, setItem] = useState(null);
  useEffect(() => {
    if (itemType === "course" && itemId) {
      api.get(`/public/courses/${itemId}`)
        .then(({ data }) => { setItem({ title: data.title, cover: data.cover_image }); trackBeginCheckout(data.price, data.currency, data.title); })
        .catch(() => setItem(null));
    } else {
      setItem(null);
    }
  }, [itemType, itemId]);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    setValidating(true);
    try {
      const { data } = await api.post("/public/promotions/validate", {
        code: coupon.trim(),
        amount: baseAmount,
        item_type: itemType,
        item_id: itemId,
      });
      setDiscount(data);
      if (data.is_free) {
        toast.success(`Cod aplicat: 100% gratuit! Acces instant după înregistrare.`);
      } else {
        toast.success(`Cod aplicat: -${data.discount} ${bank?.currency || "RON"}`);
      }
    } catch (err) {
      setDiscount(null);
      toast.error(formatApiError(err));
    } finally {
      setValidating(false);
    }
  };

  const submitFree = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      return toast.error("Completează numele și email-ul pentru a primi accesul.");
    }
    if (!form.phone.trim()) {
      return toast.error("Completează numărul de telefon de contact.");
    }
    if (!itemId) {
      return toast.error("Produs lipsă din URL.");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/checkout/free", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone,
        item_type: itemType,
        item_id: itemId,
        code: coupon.trim(),
        sms_opt_in: true,
        newsletter_opt_in: true,
      });
      toast.success("Acces acordat! Te redirecționăm...");
      await refreshAuth();
      navigate(data.redirect || "/cont");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitStripe = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      return toast.error("Completează numele și email-ul.");
    }
    if (!form.phone.trim()) {
      return toast.error("Completează numărul de telefon de contact.");
    }
    if (!itemId) {
      return toast.error("Produs lipsă din URL.");
    }
    setSubmitting(true);
    try {
      if (gift.on && !gift.email.trim()) {
        setSubmitting(false);
        return toast.error("Scrie email-ul persoanei căreia dăruiești cursul.");
      }
      const { data } = await api.post("/public/checkout/stripe", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone,
        item_type: itemType,
        item_id: itemId,
        code: coupon.trim(),
        origin_url: window.location.origin,
        sms_opt_in: true,
        newsletter_opt_in: true,
        gift_recipient_email: gift.on ? gift.email.trim().toLowerCase() : "",
        gift_recipient_name: gift.on ? gift.name.trim() : "",
        gift_message: gift.on ? gift.message.trim() : "",
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Sesiunea de plată nu a putut fi creată.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitBankTransfer = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      return toast.error("Completează numele și email-ul — pe el primești confirmarea accesului.");
    }
    if (!form.phone.trim()) {
      return toast.error("Completează numărul de telefon de contact.");
    }
    if (!itemId) {
      return toast.error("Produs lipsă din URL.");
    }
    if (gift.on && !gift.email.trim()) {
      return toast.error("Scrie email-ul persoanei căreia dăruiești cursul.");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/checkout/bank-transfer", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone,
        item_type: itemType,
        item_id: itemId,
        code: coupon.trim(),
        newsletter_opt_in: true,
        gift_recipient_email: gift.on ? gift.email.trim().toLowerCase() : "",
        gift_recipient_name: gift.on ? gift.name.trim() : "",
        gift_message: gift.on ? gift.message.trim() : "",
      });
      toast.success(`Comandă înregistrată — referință ${data.order?.reference}`);
      // Persistăm datele de confirmare ca /confirmare să reziste la refresh / redeschidere tab
      // (state-ul de navigare se pierde altfel). Le citește CheckoutConfirmation ca fallback.
      try { sessionStorage.setItem("ct_pending_transfer", JSON.stringify(data)); } catch { /* storage indisponibil — ignorăm */ }
      navigate("/confirmare", { state: { pending_transfer: data } });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitClick = () => {
    if (isFreeCheckout) return submitFree();
    if (method === "transfer") return submitBankTransfer();
    // Card / Apple / Google / Revolut / Wise → Stripe Checkout
    return submitStripe();
  };

  return (
    <div data-testid="checkout-page" style={{ background: "var(--ct-bg)" }} className="min-h-screen">
      {/* Checkout header — 112px desktop / 92px mobile per spec */}
      <header className="border-b" style={{ borderColor: "var(--ct-border)", background: "var(--ct-bg)" }}>
        <div className="max-w-[1280px] mx-auto h-[92px] lg:h-[112px] px-4 sm:px-6 lg:px-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="checkout-brand">
            <BrandLogo size={42} />
            <span className="font-serif text-lg sm:text-xl">{brand}</span>
          </Link>
          <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-[0.22em] px-3 py-1.5 rounded-full" style={{ background: "var(--ct-surface)", border: "1px solid var(--ct-gold)", color: "var(--ct-link)" }}>
            <Lock size={12} style={{ color: "var(--ct-gold)" }} /> Plată securizată
          </div>
        </div>
      </header>

      {/* Main container — max 1280, padding 56px 48px 70px per spec */}
      <div className="max-w-[1280px] mx-auto px-4 lg:px-12 pt-6 lg:pt-14 pb-12 lg:pb-[70px]">
        <div className="mb-8 sm:mb-12">
          <span className="eyebrow">Aproape gata</span>
          <h1 className="font-serif mt-3 leading-[1.05] lg:leading-[1]" style={{ fontSize: "clamp(46px, 6vw, 72px)" }}>Finalizează înscrierea</h1>
          <p className="text-sm sm:text-base mt-2 sm:mt-3" style={{ color: "var(--ct-text-muted)" }}>Completează datele, alege metoda de plată — accesul vine instant pe email.</p>
        </div>

        <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-6 lg:gap-[72px]">
          {/* LEFT: form + payment methods */}
          <div className="space-y-5 lg:space-y-7">
            <div className="card-premium p-5 sm:p-7 lg:p-8" data-testid="checkout-form-card">
              <h2 className="font-serif text-xl sm:text-2xl mb-4 sm:mb-5">Datele tale</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] block mb-1" style={{ color: "var(--ct-gold)" }}>Nume complet</label>
                  <input required className="premium-input" placeholder="Numele tău" value={form.name} onChange={onChange("name")} data-testid="checkout-name" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.18em] block mb-1" style={{ color: "var(--ct-gold)" }}>Email</label>
                    <input required type="email" className="premium-input" placeholder="email@exemplu.com" value={form.email} onChange={onChange("email")} data-testid="checkout-email" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.18em] block mb-1" style={{ color: "var(--ct-gold)" }}>Telefon</label>
                    <input required className="premium-input" placeholder="+40 7XX XXX XXX" value={form.phone} onChange={onChange("phone")} data-testid="checkout-phone" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] block mb-1" style={{ color: "var(--ct-gold)" }}>Țară</label>
                  <input className="premium-input" value={form.country} onChange={onChange("country")} data-testid="checkout-country" />
                </div>
              </div>
            </div>

            <div className="card-premium p-5 sm:p-7 lg:p-8" data-testid="checkout-payment-card">
              <h2 className="font-serif text-xl sm:text-2xl mb-4 sm:mb-5">
                {isFreeCheckout ? "Acces gratuit" : "Metodă de plată"}
              </h2>
              {isFreeCheckout ? (
                <div className="p-4 rounded-xl flex items-start gap-3" style={{ background: "var(--ct-bg-soft)", border: "1px solid var(--ct-gold)" }}>
                  <Gift size={20} style={{ color: "var(--ct-link)" }} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-sm">Plata nu este necesară</p>
                    <p className="text-xs mt-1" style={{ color: "var(--ct-text-muted)" }}>
                      Cu codul aplicat, acest produs este 100% gratuit. Completează datele tale și primești acces instant.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-[14px]">
                  {PAYMENT_METHODS.map((m) => {
                    const Ic = m.I;
                    const active = method === m.v;
                    return (
                      <button
                        key={m.v}
                        type="button"
                        onClick={() => setMethod(m.v)}
                        className="relative rounded-xl text-xs sm:text-sm flex items-center justify-center gap-2 transition-all"
                        style={{
                          height: "82px",
                          borderColor: active ? "var(--ct-emerald)" : "var(--ct-border)",
                          background: active ? "var(--ct-surface-2)" : "var(--ct-surface)",
                          borderWidth: active ? 2 : 1,
                          borderStyle: "solid",
                        }}
                        data-testid={`pay-method-${m.v}`}
                      >
                        <Ic size={18} style={{ color: "var(--ct-gold)" }} />
                        <span className="font-medium">{m.l}</span>
                        {active && (
                          <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: "var(--ct-emerald)" }}>
                            <CheckCircle2 size={11} style={{ color: "var(--ct-gold)" }} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Bank transfer details panel — shown when method=transfer */}
              {!isFreeCheckout && method === "transfer" && (
                <div className="mt-5 p-4 sm:p-5 rounded-xl border" style={{ borderColor: "var(--ct-gold)", background: "var(--ct-bg-soft)" }} data-testid="bank-transfer-panel">
                  <p className="text-sm font-medium mb-3 flex items-center gap-2"><Banknote size={16} style={{ color: "var(--ct-gold)" }} /> Date pentru transfer</p>
                  <BankTransferDetails bank={bank} />
                </div>
              )}

              {/* Dăruiește un curs (16.23) — pe orice metodă de plată */}
              {!isFreeCheckout && itemType === "course" && (
                <div className="mt-4 p-4 rounded-xl border" style={{ borderColor: "var(--ct-border)" }} data-testid="gift-panel">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input type="checkbox" className="accent-[var(--ct-emerald)]" checked={gift.on} onChange={(e) => setGift({ ...gift, on: e.target.checked })} data-testid="gift-toggle" />
                    <span className="text-sm">🎁 Dăruiește cursul cuiva drag (accesul merge la el, nu la tine)</span>
                  </label>
                  {gift.on && (
                    <div className="mt-3 space-y-2">
                      <input className="premium-input" placeholder="Email-ul persoanei" value={gift.email} onChange={(e) => setGift({ ...gift, email: e.target.value })} data-testid="gift-email" />
                      <input className="premium-input" placeholder="Numele ei (opțional)" value={gift.name} onChange={(e) => setGift({ ...gift, name: e.target.value })} />
                      <textarea className="premium-input resize-none" rows={2} placeholder="Un mesaj pentru ea (opțional)" value={gift.message} onChange={(e) => setGift({ ...gift, message: e.target.value })} maxLength={500} />
                      <p className="text-[11px]" style={{ color: "var(--ct-text-dim)" }}>După confirmarea plății, îi trimitem accesul + mesajul tău pe email.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: order summary */}
          <aside className="lg:sticky lg:top-6 h-fit space-y-5">
            <div className="card-premium p-5 sm:p-7 lg:p-8" data-testid="order-summary">
              <h3 className="font-serif text-xl sm:text-2xl mb-4 sm:mb-5">Rezumat comandă</h3>
              <div className="flex items-start gap-3 sm:gap-4 mb-5">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden shrink-0" style={{ background: "var(--ct-surface-2)", border: "1px solid var(--ct-border)" }}>
                  {item?.cover && <img src={item.cover} alt={item.title || "Produs"} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-base sm:text-lg leading-tight">{item?.title || product || "Produsul tău"}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--ct-text-muted)" }}>Acces imediat după plată</p>
                  <ul className="mt-2 space-y-1">
                    {["Acces complet la conținut", "Suport prin email", ...(guaranteeDays > 0 ? [`Garanție ${guaranteeDays} zile`] : [])].map((b, i) => (
                      <li key={i} className="text-[11px] sm:text-xs flex items-center gap-1.5" style={{ color: "var(--ct-text)" }}>
                        <CheckCircle2 size={11} style={{ color: "var(--ct-gold)" }} /> {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Coupon input */}
              <div className="mb-4 p-3 rounded-lg" style={{ background: "var(--ct-bg-soft)" }} data-testid="coupon-input-card">
                <p className="text-[10px] uppercase tracking-[0.18em] mb-1.5 flex items-center gap-1.5" style={{ color: "var(--ct-gold)" }}>
                  <TagIcon size={11} /> Ai un cod de reducere?
                </p>
                <div className="flex gap-2">
                  <input
                    placeholder="EX: IUNIE20"
                    className="premium-input !text-sm !py-2 uppercase"
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                    data-testid="coupon-input"
                    disabled={!!discount}
                  />
                  {!discount ? (
                    <button type="button" onClick={applyCoupon} disabled={validating || !coupon.trim()} className="btn-outline-gold !py-2 !px-3 !text-xs whitespace-nowrap" data-testid="coupon-apply">
                      {validating ? "..." : "Aplică"}
                    </button>
                  ) : (
                    <button type="button" onClick={() => { setDiscount(null); setCoupon(""); }} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="coupon-clear">
                      Renunță
                    </button>
                  )}
                </div>
                {discount && (
                  <p className="text-xs mt-2 flex items-center gap-1.5" style={{ color: "var(--ct-link)" }}>
                    <Sparkles size={11} style={{ color: "var(--ct-gold)" }} /> {discount.name || coupon}: -{discount.discount} {bank?.currency || "RON"}
                  </p>
                )}
              </div>

              {/* Totals */}
              {baseAmount > 0 && (
                <div className="mb-4 pt-3 border-t space-y-1.5" style={{ borderColor: "var(--ct-border)" }}>
                  <div className="flex items-center justify-between text-sm">
                    <span style={{ color: "var(--ct-text-muted)" }}>Subtotal</span>
                    <span>{baseAmount} {bank?.currency || "RON"}</span>
                  </div>
                  {discount && (
                    <div className="flex items-center justify-between text-sm">
                      <span style={{ color: "var(--ct-link)" }}>Reducere</span>
                      <span style={{ color: "var(--ct-link)" }}>-{discount.discount} {bank?.currency || "RON"}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-base font-medium pt-1.5 border-t" style={{ borderColor: "var(--ct-border)" }}>
                    <span>Total de plată</span>
                    <span className="font-serif text-xl" data-testid="checkout-total">
                      {isFreeCheckout ? (
                        <span style={{ color: "var(--ct-link)" }}>GRATUIT</span>
                      ) : (
                        <>{total} {bank?.currency || "RON"}</>
                      )}
                    </span>
                  </div>
                </div>
              )}

              {isFreeCheckout && (
                <div className="mb-4 p-3 rounded-lg flex items-start gap-2.5" style={{ background: "rgba(13,27,42,0.08)", border: "1px solid var(--ct-emerald)" }} data-testid="free-checkout-banner">
                  <Gift size={16} style={{ color: "var(--ct-link)" }} className="mt-0.5 shrink-0" />
                  <p className="text-xs leading-relaxed" style={{ color: "var(--ct-text)" }}>
                    Promoția aplică <strong>100% reducere</strong>. Apasă pe buton — îți creăm contul, primești acces instant la curs și o parolă pe email.
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={onSubmitClick}
                disabled={submitting || (!isFreeCheckout && !itemId)}
                className={`w-full rounded-[10px] font-semibold transition-all inline-flex items-center justify-center gap-2 hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed`}
                style={{ height: "64px", fontSize: "24px", background: "var(--ct-emerald)", color: "var(--ct-text-inverse)" }}
                data-testid="checkout-submit"
              >
                {isFreeCheckout ? (
                  <>
                    <Gift size={20} style={{ color: "var(--ct-gold)" }} />
                    {submitting ? "Se activează..." : "Activează accesul GRATUIT"}
                  </>
                ) : (
                  <>
                    <Lock size={20} style={{ color: "var(--ct-gold)" }} />
                    <span>{method === "transfer" ? (submitting ? "Se înregistrează..." : "Trimite comanda") : (submitting ? "Se redirecționează..." : "Plătește și primește acces")}</span>
                  </>
                )}
              </button>
              <p className="text-[10px] text-center mt-3" style={{ color: "var(--ct-text-dim)" }}>
                {isFreeCheckout
                  ? "Cu codul aplicat, acest produs este 100% gratuit. Apasă pentru a-ți activa contul și a primi acces instant."
                  : method === "transfer"
                  ? "Comanda se înregistrează cu o referință de plată. Faci transferul cu referința în descriere, iar accesul se activează la confirmarea încasării (de regulă în aceeași zi)."
                  : "Procesare prin Stripe (Card / Apple Pay / Google Pay / Revolut / Wise). 3D Secure activat."}
              </p>
            </div>
          </aside>
        </div>

        {/* Trust badges */}
        <div className="mt-10 lg:mt-14 pt-8 lg:pt-12 border-t" style={{ borderColor: "var(--ct-border)" }} data-testid="trust-badges">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {trustBadges.map((b, i) => {
              const Ic = TRUST_ICONS[i % TRUST_ICONS.length];
              return (
                <div key={i} className="text-center sm:text-left">
                  <Ic size={22} style={{ color: "var(--ct-gold)" }} className="mx-auto sm:mx-0 mb-2" />
                  <p className="text-xs sm:text-sm font-medium" style={{ color: "var(--ct-text)" }}>{b.title}</p>
                  <p className="text-[10px] sm:text-xs mt-0.5" style={{ color: "var(--ct-text-muted)" }}>{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CheckoutConfirmation() {
  const location = useLocation();
  // La refresh, state-ul de navigare dispare → cădem pe copia persistată în sessionStorage.
  const pending = location.state?.pending_transfer || (() => {
    try { return JSON.parse(sessionStorage.getItem("ct_pending_transfer") || "null"); }
    catch { return null; }
  })();

  // Variantă „comandă prin transfer în așteptare": referință + date bancare
  if (pending?.order) {
    const { order, bank_transfer: bank, instructions } = pending;
    return (
      <div data-testid="confirmation-page" className="min-h-screen flex items-center justify-center px-5 py-12" style={{ background: "var(--ct-bg)" }}>
        <div className="max-w-xl w-full card-premium p-8 sm:p-12">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: "var(--ct-emerald)", border: "2px solid var(--ct-gold)" }}>
              <Banknote size={34} style={{ color: "var(--ct-gold)" }} />
            </div>
            <span className="eyebrow">Comandă înregistrată</span>
            <h1 className="font-serif text-4xl sm:text-5xl mt-3 mb-3">Un singur pas rămas</h1>
            <p className="text-sm sm:text-base mb-6" style={{ color: "var(--ct-text-muted)" }}>
              Fă transferul de mai jos, iar accesul la <strong>{order.item_title}</strong> se activează la confirmarea încasării — de regulă în aceeași zi.
            </p>
          </div>
          <div className="p-4 rounded-xl mb-5 text-center" style={{ background: "var(--ct-bg-soft)", border: "1px solid var(--ct-gold)" }} data-testid="transfer-reference">
            <p className="text-[10px] uppercase tracking-[0.22em] mb-1" style={{ color: "var(--ct-gold)" }}>Referință plată (pune-o în descrierea transferului)</p>
            <p className="font-mono text-2xl font-semibold">{order.reference}</p>
            <p className="text-sm mt-1.5">Sumă: <strong>{order.amount} {order.currency}</strong></p>
          </div>
          <div className="p-4 sm:p-5 rounded-xl border mb-6" style={{ borderColor: "var(--ct-border)", background: "var(--ct-surface)" }}>
            <p className="text-sm font-medium mb-3 flex items-center gap-2"><Banknote size={16} style={{ color: "var(--ct-gold)" }} /> Date pentru transfer</p>
            <BankTransferDetails bank={bank} />
          </div>
          {instructions && (
            <p className="text-xs leading-relaxed mb-6" style={{ color: "var(--ct-text-muted)" }}>{instructions}</p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" className="btn-outline-gold !py-3 justify-center" data-testid="confirm-home">Pagina principală</Link>
            <Link to="/contact" className="btn-ghost !py-3 justify-center">Am o întrebare</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="confirmation-page" className="min-h-screen flex items-center justify-center px-5 py-12" style={{ background: "var(--ct-bg)" }}>
      <div className="max-w-xl w-full card-premium p-8 sm:p-12 text-center">
        <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: "var(--ct-emerald)", border: "2px solid var(--ct-gold)" }}>
          <CheckCircle2 size={36} style={{ color: "var(--ct-gold)" }} />
        </div>
        <span className="eyebrow">Comandă confirmată</span>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3 mb-4">Mulțumesc din suflet!</h1>
        <p className="text-sm sm:text-base mb-6" style={{ color: "var(--ct-text-muted)" }}>
          Comanda ta a fost înregistrată. Vei primi un email de confirmare cu accesul în câteva minute.
        </p>
        <div className="border-t pt-6 mb-6 text-left" style={{ borderColor: "var(--ct-border)" }}>
          <p className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: "var(--ct-gold)" }}>Pașii următori</p>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: "var(--ct-gold)" }} /> Verifică emailul — primești factura PDF</li>
            <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: "var(--ct-gold)" }} /> Intră în „Contul meu&rdquo; → cursul tău e activ</li>
            <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: "var(--ct-gold)" }} /> Alătură-te comunității WhatsApp pentru suport</li>
          </ul>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/cont" className="btn-primary !py-3 justify-center" data-testid="confirm-account">Contul meu</Link>
          <Link to="/" className="btn-secondary !py-3 justify-center" data-testid="confirm-home">Pagina principală</Link>
        </div>
      </div>
    </div>
  );
}
